import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/cn';

export type ToastKind = 'success' | 'error' | 'info';
export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  action?: { label: string; onClick: () => void } | undefined;
}

interface ToastApi {
  toast: (message: string, kind?: ToastKind, action?: ToastItem['action']) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);

  const toast = useCallback(
    (message: string, kind: ToastKind = 'info', action?: ToastItem['action']) => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-3), { id, kind, message, action }]);
      window.setTimeout(() => dismiss(id), kind === 'error' ? 7000 : 4000);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      toast,
      success: (m) => toast(m, 'success'),
      error: (m) => toast(m, 'error'),
      info: (m) => toast(m, 'info'),
    }),
    [toast],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed right-5 bottom-5 z-[60] flex flex-col gap-2"
        aria-live="polite"
        aria-atomic="false"
      >
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            className={cn(
              'toast pointer-events-auto flex items-center gap-2 rounded-lg px-3.5 py-2.5 font-semibold shadow-lg',
              'bg-ink text-on-ink',
            )}
          >
            {t.kind === 'success' ? (
              <CheckCircle2 className="size-[18px] text-[var(--toast-ok)]" />
            ) : t.kind === 'error' ? (
              <AlertTriangle className="size-[18px] text-warn" />
            ) : (
              <Info className="size-[18px] opacity-80" />
            )}
            <span>{t.message}</span>
            {t.action ? (
              <button type="button" className="ml-2 underline" onClick={t.action.onClick}>
                {t.action.label}
              </button>
            ) : null}
            <button
              type="button"
              aria-label="Dismiss"
              className="ml-1 opacity-60 hover:opacity-100"
              onClick={() => dismiss(t.id)}
            >
              <X className="size-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
